<!--
This component is responsible for drawing the actual chart with legend, additional information regarding the chart and a temporarily visible info box to indicate changes of the investmentStrategy that happen automatically in the background.

The actual chart (svg) is created with the d3 npm package: https://d3js.org/
Animations/Transitions are realized with d3-interpolate-path: https://github.com/pbeshai/d3-interpolate-path

The chart essentially shows the expected minimum, maximum and average outcome versus time for a given investment.
To this end, three lines (lineDataLow, lineDataAverage, lineDataHigh) are drawn which depend on the "input parameters" investmentHorizon, investmentAmount and investmentStrategySelected.
Watchers on these parameters are used to update the chart whenever one of them changes its value.
An additional watcher is used to register changes of the viewport's width which is needed to re-render the diagram in such cases.

There are three key methods:

- createSVG:
According to its name, it creates the actual svg html tag.
Most other methods are simple convenience functions that add small parts (group tags) to the svg.
An example would be the drawAxis function which adds the x/y axes to the diagram.

- updateData:
This can be seen as the entry point of the component.
It is responsible for fetching the chart data from the backend and (re-)drawing the chart with it.
In order to avoid unreasonably many backend calls when the input parameters change often, this function uses lodash's debounce mechanism.
This is the function that is called from the three watchers mentioned above.

- onChartClick:
This function handles click events: It places the circles together with their lables to the x-tick position that is closest to the clicked location on the chart.

Note:
The margin properties of the diagram are set in the components state rather than via CSS.
This is necessary because the margin information is required several times in different convenience methods mentioned above.
-->

<template>
    <div
        class="investment-strategy-chart">
        <div v-if="displayChart">
            <!-- (desktop only) chart-related information -->
            <div class="investment-strategy-chart__infos d-none d-lg-block">
                <p class="mb-0">
                    {{ $t('investmentStrategyChart.typeText', chartInfoTranslations) }}
                    {{ $t('investmentStrategyChart.typeTextAdd') }}
                </p>
            </div>

            <!-- actual chart with loading spinner -->
            <div
                ref="investmentStrategyChart"
                class="investment-strategy-chart__wrapper">
                <div :class="{ 'investment-strategy-chart__loader': true, 'investment-strategy-chart__loader--active': chartIsUpdating }" />
            </div>

            <!-- (mobile) chart legend -->
            <div class="row">
                <div class="col-lg-10 offset-lg-1">
                    <div class="investment-strategy-chart__legend d-float">
                        <ul class="list-unstyled">
                            <li class="investment-strategy-chart__legend-item">
                                <span class="investment-strategy-chart__legend-color investment-strategy-chart__legend-color--high" /><span class="investment-strategy-chart__legend-text">{{ $t('investmentStrategyChart.expectationHigh') }}</span>
                            </li>
                            <li class="investment-strategy-chart__legend-item">
                                <span class="investment-strategy-chart__legend-color investment-strategy-chart__legend-color--average" /><span class="investment-strategy-chart__legend-text">{{ $t('investmentStrategyChart.expectationAverage') }}</span>
                            </li>
                            <li class="investment-strategy-chart__legend-item">
                                <span class="investment-strategy-chart__legend-color  investment-strategy-chart__legend-color--low" /><span class="investment-strategy-chart__legend-text">{{ $t('investmentStrategyChart.expectationLow') }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- info box, indicating automatic background changes of investmentStrategySelected -->
            <div class="row">
                <div class="col">
                    <transition name="fade">
                        <investment-strategy-change-notification
                            v-if="previousInvestmentStrategySelected && this.viewportNoMatch"
                            tense="past" />
                    </transition>
                </div>
            </div>

            <!-- (mobile only) chart-related information -->
            <div class="row d-lg-none">
                <div class="col">
                    <div class="investment-strategy-chart__infos investment-strategy-chart__infos--no-margin">
                        <p class="mb-0">
                            {{ $t('investmentStrategyChart.typeText', chartInfoTranslations) }} {{ $t('investmentStrategyChart.typeTextAdd') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as d3 from 'd3';
import { mapGetters, mapActions } from 'vuex';
import { debounce } from 'lodash';
import { InvestmentStrategyType } from '@/api/enums';
import { interpolatePath } from 'd3-interpolate-path';
import WindowInstance from '@/components/common/WindowInstance';
import InvestmentStrategyChangeNotification from '@/components/InvestmentStrategyChangeNotification';

export default {
    name: 'InvestmentStrategyChart',
    components: {
        InvestmentStrategyChangeNotification,
    },
    data () {
        return {
            chart: null,
            lineGroup: null,
            pointGroup: null,
            labelGroup: null,
            investmentHorizonInfoGroup: null,
            labelPositionsY: {},
            scaleX: null,
            scaleYInverted: null,
            lineFunc: null,
            areaLow: null,
            areaHigh: null,
            chartWidth: null,
            chartHeight: null,
            chartIsUpdating: false,
            minimumTickWidth: 50, // Determines number of x ticks on small viewports
            labelWidth: 75,
            labelHeight: 30,
            monthsOrYearsThreshold: 3, // Investment horizon treshold in years to switch displaying the x-axis from months to years
            defaultChartHeightLg: 340,
            defaultChartHeightXs: 200,
            defaultTransition: 750,
            InvestmentStrategyType,
            interpolatePath,
        };
    },
    watch: {
        investmentAmount () {
            this.updateData({ reset: true });
        },
        investmentHorizon () {
            this.updateData({ reset: true });
        },
        investmentStrategySelected: {
            deep: true,
            handler () {
                this.updateData();
            },
        },
        windowWidth (val) {
            this.drawChart(true);
        },
    },
    computed: {
        ...mapGetters(['investmentDiagramResponse', 'investmentStrategySelected', 'investmentAmount', 'investmentHorizon', 'viewportNoMatch', 'previousInvestmentStrategySelected']),
        windowWidth () {
            return WindowInstance.innerWidth;
        },
        investmentHorizonInMonths () {
            return this.investmentHorizon * 12;
        },
        chartInfoTranslations () {
            return {
                strategy: this.$t(`investmentStrategyType.${this.investmentStrategySelected.name}.title`),
                yearsString: this.investmentHorizon > 1 ? this.$t('common.yearsPreposition') : this.$t('common.year'),
                investmentAmount: this.investmentAmountFormatted,
                investmentHorizon: this.investmentHorizon,
                targetAsset: this.targetAsset,
            };
        },
        margin () {
            return {
                left: 0,
                right: this.viewportNoMatch ? 10 : 105,
                top: 0,
                bottom: 40,
            };
        },
        optimalXTickCountForViewport () {
            return Math.floor(this.chartWidth / this.minimumTickWidth, 10);
        },
        displayChart () {
            return this.investmentAmount > 0 && this.investmentHorizon > 0;
        },
        chartDataMonths () {
            return this.investmentDiagramResponse && this.investmentDiagramResponse.map(item => {
                return {
                    investmentStrategyType: item.investmentStrategyType,
                    month: item.month,
                    expectationLow: item.expectationLow,
                    expectationAverage: item.expectationAverage,
                    expectationHigh: item.expectationHigh,
                };
            });
        },
        chartDataYears () {
            return this.chartDataMonths && this.chartDataMonths.filter((item, index) => index === 0 || index % 12 === 0);
        },
        chartData () {
            // Get data for months or years
            const data = this.isXAxisInMonths ? this.chartDataMonths : this.chartDataYears;
            if (!data) { return; }
            // Reduce data points for narrow viewports
            if (this.optimalXTickCountForViewport >= data.length) {
                return data;
            }
            const filterNth = Math.round(data.length / this.optimalXTickCountForViewport, 10);
            return data.filter((item, index) => index % filterNth === 0 || index === 0 || index === data.length - 1);
        },
        lineDataCenter () {
            return this.chartData.map(item => {
                return {
                    x: item.month,
                    y: this.investmentAmount,
                };
            });
        },
        lineDataLow () {
            return this.chartData.map(item => {
                return {
                    x: item.month,
                    y: Math.floor(item.expectationLow),
                };
            });
        },
        lineDataAverage () {
            return this.chartData.map(item => {
                return {
                    x: item.month,
                    y: Math.floor(item.expectationAverage),
                };
            });
        },
        lineDataHigh () {
            return this.chartData.map(item => {
                return {
                    x: item.month,
                    y: Math.floor(item.expectationHigh),
                };
            });
        },
        targetAsset () {
            return this.investmentDiagramResponse && this.$options.filters.formatAmount(this.lineDataAverage[this.lineDataAverage.length - 1].y);
        },
        investmentAmountFormatted () {
            return this.$options.filters.formatAmount(this.investmentAmount);
        },
        tickValuesY () {
            return [
                this.lineDataLow[this.lineDataLow.length - 1].y,
                this.lineDataAverage[this.lineDataAverage.length - 1].y,
                this.lineDataHigh[this.lineDataHigh.length - 1].y,
            ];
        },
        tickValuesX () {
            const ticks = this.chartData.map(item => item.month);
            return ticks;
        },
        tickFormatX () {
            // Display X-ticks as months || years
            const divider = this.isXAxisInMonths ? 1 : 12;
            return n => {
                return n === 0 ? '' : n / divider;
            };
        },
        tickFormatY () {
            return n => this.$options.filters.formatAmount(n);
        },
        isXAxisInMonths () {
            return Boolean(this.investmentHorizon < this.monthsOrYearsThreshold);
        },
    },
    methods: {
        ...mapActions(['fetchInvestmentDiagramData', 'resetInvestmentDiagramData']),
        resetChart () {
            return new Promise(resolve => {
                this.resetInvestmentDiagramData();
                this.drawChart();
                setTimeout(() => {
                    resolve();
                }, this.defaultTransition);
            });
        },
        fetchDataAndDrawChart () {
            this.chartIsUpdating = true;
            this.fetchInvestmentDiagramData()
                .then(() => {
                    this.drawChart();
                    this.chartIsUpdating = false;
                });
        },
        updateData: debounce(function (params = { reset: false }) {
            if (this.investmentStrategySelected && this.investmentAmount && this.investmentHorizon) {
                if (params.reset) {
                    this.resetChart().then(() => {
                        this.fetchDataAndDrawChart();
                    });
                } else {
                    this.fetchDataAndDrawChart();
                }
            }
        }, 500, { leading: false, trailing: true }),
        onChartClick () {
            const mouse = d3.mouse(this.chart.node());
            const mouseXScaled = Math.round(this.scaleX.invert(mouse[0]));
            const closest = this.lineDataAverage.reduce(function (prev, curr) {
                return (Math.abs(curr.x - mouseXScaled) < Math.abs(prev.x - mouseXScaled) ? curr : prev);
            });
            const closestIndex = this.lineDataAverage.findIndex(item => item.x === closest.x);
            this.drawPointsAndLabels(closestIndex);
        },
        appendDot (selection, name, fillColor, lineData, x) {
            selection.append('circle')
                .attr('class', 'dot-' + name)
                .attr('r', 7)
                .attr('cx', this.scaleX(lineData[x].x))
                .attr('cy', this.scaleYInverted(lineData[x].y))
                .attr('fill', fillColor)
                .attr('stroke', fillColor)
                .attr('stroke-width', '15')
                .attr('stroke-opacity', '0.2');
        },
        appendLabel (selection, name, fillColor, lineData, x) {
            // Determine label position
            let labelPositionX = this.scaleX(lineData[x].x) - 95;
            let labelPositionY = this.scaleYInverted(lineData[x].y) + 7;

            // Collision detection with other labels
            for (const label in this.labelPositionsY) {
                const labelY = this.labelPositionsY[label];
                if (Math.abs(labelPositionY - labelY) < this.labelHeight + 5) {
                    // When colliding with another label, set the y position right above it
                    labelPositionY = labelY - this.labelHeight - 5;
                }
            }
            // Save labelPositionY
            this.labelPositionsY[name] = labelPositionY;

            // Position label to the right of the dot if close to the left edge
            if (labelPositionX < this.labelWidth / 2) {
                labelPositionX = this.scaleX(lineData[x].x) + 20;
            }

            // Append label
            const label = selection
                .append('g')
                .attr('class', 'label-' + name)
                .attr('transform', 'translate(' + labelPositionX + ', ' + labelPositionY + ')');

            label.append('rect')
                .attr('class', 'label--box')
                .attr('width', this.labelWidth)
                .attr('height', this.labelHeight)
                .attr('x', 0)
                .attr('y', 0)
                .attr('fill', fillColor)
                .attr('filter', 'url(#boxshadow)');
            label.append('text')
                .attr('class', 'label__text')
                .attr('x', Math.round(this.labelWidth / 2))
                .attr('y', Math.round(this.labelHeight / 2 + 3))
                .text(() => {
                    return this.$options.filters.formatAmount(lineData[x].y);
                })
                .attr('text-anchor', 'middle')
                .attr('fill', '#ffffff');
        },
        drawPointsAndLabels (x, delay = 0) {
            // Add groups for points and labels to LineGroup
            if (this.pointGroup !== null) {
                this.pointGroup.remove();
            }
            if (this.labelGroup !== null) {
                this.labelGroup.remove();
            }

            if (this.labelPositionsY.length !== 0) {
                this.labelPositionsY = {};
            }

            // Don't render points and labels during reset animation
            if (this.lineDataHigh[this.lineDataHigh.length - 1].y === this.lineDataLow[this.lineDataLow.length - 1].y) {
                return;
            }

            this.pointGroup = this.lineGroup.append('g')
                .attr('class', 'point-group');

            this.labelGroup = this.lineGroup.append('g')
                .attr('class', 'label-group');

            // Transition opacity
            if (delay > 0) {
                const pointGroupSelection = this.lineGroup.select('.point-group');
                pointGroupSelection
                    .style('opacity', '0')
                    .transition()
                    .delay(delay)
                    .style('opacity', '1');

                const labelGroupSelection = this.lineGroup.select('.label-group');
                labelGroupSelection
                    .style('opacity', '0')
                    .transition()
                    .delay(delay)
                    .style('opacity', '1');
            }

            // Append dots and labels
            if (x > 0) {
                this.appendDot(this.pointGroup, 'low', '#FF8C28', this.lineDataLow, x);
                this.appendDot(this.pointGroup, 'high', '#4F1F68', this.lineDataHigh, x);
            }
            this.appendDot(this.pointGroup, 'average', '#D9007E', this.lineDataAverage, x);

            if (x > 0) {
                this.appendLabel(this.labelGroup, 'low', '#FF8C28', this.lineDataLow, x);
            }
            this.appendLabel(this.labelGroup, 'average', '#D9007E', this.lineDataAverage, x);
            if (x > 0) {
                this.appendLabel(this.labelGroup, 'high', '#4F1F68', this.lineDataHigh, x);
            }
        },
        drawGridLines () {
            // Gridlines
            // add the X gridlines
            const gridLines = this.chart.selectAll('.grid-lines')
                .data([this.chartData])
                .attr('class', 'grid-lines');
            gridLines.exit().remove();
            gridLines.enter()
                .append('g')
                .attr('class', 'grid-lines')
                .attr('transform', 'translate(' + (this.margin.left) + ',' + (this.chartHeight) + ')')
                .call(d3.axisBottom(this.scaleX)
                    .tickValues(this.tickValuesX)
                    .tickSize(-this.chartHeight)
                    .tickSizeOuter(0)
                    .tickFormat(''),
                )
                .call(g => g.select('.domain').remove())
                .merge(gridLines)
                .transition().duration(this.defaultTransition)
                .call(d3.axisBottom(this.scaleX)
                    .tickValues(this.tickValuesX)
                    .tickSize(-this.chartHeight)
                    .tickSizeOuter(0)
                    .tickFormat(''),
                );
        },
        appendArea (selection, name, chartData, areaFunc) {
            const area = selection
                .data([chartData])
                .attr('class', name);
            area.exit().remove();
            area.enter()
                .append('path')
                .attr('class', name)
                .data([chartData])
                .attr('d', areaFunc)
                .merge(selection)
                .transition().duration(this.defaultTransition)
                .attrTween('d', d => {
                    const previous = area.size() > 0 ? area.attr('d') : null;
                    const current = areaFunc(d);
                    return interpolatePath(previous, current);
                })
            ;
        },
        drawAreas () {
            // Areas
            this.areaLow = d3.area()
                .curve(d3.curveCardinal)
                .x(d => this.scaleX(d.month))
                .y0(d => this.scaleYInverted(Math.floor(d.expectationLow)))
                .y1(d => this.scaleYInverted(Math.floor(d.expectationAverage)));

            this.areaHigh = d3.area()
                .curve(d3.curveCardinal)
                .x(d => this.scaleX(d.month))
                .y0(d => this.scaleYInverted(Math.floor(d.expectationAverage)))
                .y1(d => this.scaleYInverted(Math.floor(d.expectationHigh)));

            this.appendArea(this.lineGroup.selectAll('.area-low'), 'area-low', this.chartData, this.areaLow);
            this.appendArea(this.lineGroup.selectAll('.area-high'), 'area-high', this.chartData, this.areaHigh);
        },
        appendLine (selection, lineClass, strokeColor, lineData) {
            const line = selection
                .data([lineData])
                .attr('class', 'line ' + lineClass);
            line.exit().remove();
            line.enter()
                .append('path')
                .attr('class', 'line ' + lineClass)
                .attr('d', this.lineFunc(lineData))
                .attr('stroke', strokeColor)
                .attr('stroke-width', 5)
                .attr('fill', 'none')
                .merge(line)
                .transition().duration(this.defaultTransition)
                .attrTween('d', d => {
                    const previous = line.size() > 0 ? line.attr('d') : null;
                    const current = this.lineFunc(d);
                    return interpolatePath(previous, current);
                });
        },
        drawLines (forceRerender = false) {
            this.lineFunc = d3.line()
                .x(d => { return this.scaleX(d.x); })
                .y(d => { return this.scaleYInverted(d.y); })
                .curve(d3.curveCardinal);

            // Create a group for all lines (to position)
            if (!this.lineGroup || forceRerender) {
                this.lineGroup = this.chart.append('g')
                    .attr('class', 'line-group')
                    .attr('clip-path', 'url(#chartClipPath)')
                    .attr('transform', 'translate(' + this.margin.left + ', ' + this.margin.top + ')');
            }

            // Move line-group to front
            this.chart.selectAll('.line-group').raise();

            // Append lines
            this.appendLine(this.lineGroup.selectAll('.line--center'), 'line--center', '#ffffff', this.lineDataCenter);
            this.appendLine(this.lineGroup.selectAll('.line--low'), 'line--low', '#FF8C28', this.lineDataLow);
            this.appendLine(this.lineGroup.selectAll('.line--average'), 'line--average', '#D9007E', this.lineDataAverage);
            this.appendLine(this.lineGroup.selectAll('.line--high'), 'line--high', '#4F1F68', this.lineDataHigh);
        },
        createScales () {
            // Scales
            this.scaleX = d3.scaleLinear()
                .domain([0, d3.max(this.chartData, function (d) { return d.month; })])
                .range([0, this.chartWidth]);

            this.scaleYInverted = d3.scaleLinear()
                .domain([d3.min(this.chartData, function (d) { return d.expectationLow; }), d3.max(this.chartData, function (d) { return d.expectationHigh; })])
                .range([this.chartHeight - (this.labelHeight + 20), this.labelHeight]);
        },
        drawAxis () {
            const xPositionOffset = 10;

            // Axis X ---
            const axisX = d3.axisBottom()
                .scale(this.scaleX)
                .tickValues(this.tickValuesX)
                .tickFormat(this.tickFormatX)
                .tickSize(0);

            const axisXselection = this.chart.selectAll('.axis-x')
                .data([this.chartData])
                .attr('class', 'axis-x');
            axisXselection.exit().remove();
            axisXselection.enter()
                .append('g')
                .attr('class', 'axis-x')
                .attr('transform', 'translate(' + this.margin.left + ',' + (this.chartHeight + 20) + ')')
                .call(axisX)
                .merge(axisXselection)
                .transition().duration(this.defaultTransition)
                .call(axisX);

            const timeUnit = this.isXAxisInMonths ? (this.viewportNoMatch ? this.$t('investmentStrategyChart.timeUnit.months.short') : this.$t('investmentStrategyChart.timeUnit.months.long')) : (this.viewportNoMatch ? this.$t('investmentStrategyChart.timeUnit.years.short') : this.$t('investmentStrategyChart.timeUnit.years.long'));

            this.chart.select('.time-unit').remove();
            this.chart.append('g')
                .attr('class', 'time-unit')
                .attr('transform', 'translate(' + (this.chartWidth + xPositionOffset) + ',' + (this.chartHeight + 30) + ')')
                .append('text')
                .text(timeUnit)
                .attr('x', this.viewportNoMatch ? -25 : 3)
                .attr('y', 3);

            // Axis Y ---
            const axisY = d3.axisRight()
                .scale(this.scaleYInverted)
                .tickValues(this.tickValuesY)
                .tickFormat(this.tickFormatY)
                .tickSize(0);

            const axisYselection = this.chart.selectAll('.axis-y')
                .data([this.chartData])
                .attr('class', 'axis-y');
            axisYselection.exit().remove();
            axisYselection.enter()
                .append('g')
                .attr('class', 'axis-y')
                .attr('transform', 'translate(' + (this.chartWidth + xPositionOffset) + ',' + this.margin.top + ')')
                .call(axisY)
                .merge(axisYselection)
                .transition().duration(this.defaultTransition)
                .call(axisY);

            // Currency
            this.chart.append('g')
                .attr('class', 'currency')
                .attr('transform', 'translate(' + (this.chartWidth + xPositionOffset) + ',' + (this.margin.top + 12) + ')')
                .append('text')
                .text('CHF')
                .attr('x', 3)
                .attr('y', 0);
        },
        drawInvestmentHorizonInfo () {
            this.chart.select('.investment-horizon-info-group').remove();
            this.investmentHorizonInfoGroup = this.chart.append('g')
                .attr('class', 'investment-horizon-info-group')
                .attr('transform', 'translate(' + 5 + ', ' + 5 + ')'); ;

            this.investmentHorizonInfoGroup.append('rect')
                .attr('class', 'investment-horizon-info__box')
                .attr('width', 130)
                .attr('height', 46)
                .attr('x', 0)
                .attr('y', 0)
                .attr('fill', '#ffffff');

            this.investmentHorizonInfoGroup.append('text')
                .attr('class', 'investment-horizon-info__text')
                .text(() => {
                    return this.$t('investmentStrategyChart.investmentHorizon');
                })
                .attr('x', 10)
                .attr('y', 15)
                .attr('fill', '#50585B');

            this.investmentHorizonInfoGroup.append('text')
                .attr('class', 'investment-horizon-info__amount')
                .text(() => {
                    return this.investmentHorizon < this.monthsOrYearsThreshold ? this.investmentHorizonInMonths + ' ' + this.$t('investmentStrategyChart.timeUnit.months.long') : this.investmentHorizon + ' ' + this.$t('common.years');
                })
                .attr('x', 10)
                .attr('y', 35)
                .attr('fill', '#50585B');
        },
        drawLegendForDesktop () {
            this.chart.select('.investment-strategy-chart-desktop-legend').remove();
            this.legend = this.chart.append('g')
                .attr('class', 'investment-strategy-chart-desktop-legend__box')
                .attr('transform', 'translate(' + 5 + ', ' + 5 + ')'); ;

            this.legend.append('rect')
                .attr('class', 'investment-strategy-chart-desktop-legend__box')
                .attr('width', 160)
                .attr('height', 75)
                .attr('x', 0)
                .attr('y', 0)
                .attr('fill', '#ffffff');

            this.legend.append('rect')
                .attr('class', 'investment-strategy-chart-desktop-legend__rect')
                .attr('width', 15)
                .attr('height', 15)
                .attr('x', 10)
                .attr('y', 10)
                .attr('fill', '#4F1F68');

            this.legend.append('text')
                .attr('class', 'investment-strategy-chart-desktop-legend__text')
                .text(() => {
                    return this.$t('investmentStrategyChart.expectationHigh');
                })
                .attr('x', 30)
                .attr('y', 22)
                .attr('fill', '#50585B');

            this.legend.append('rect')
                .attr('class', 'investment-strategy-chart-desktop-legend__rect')
                .attr('width', 15)
                .attr('height', 15)
                .attr('x', 10)
                .attr('y', 30)
                .attr('fill', '#D9007E');

            this.legend.append('text')
                .attr('class', 'investment-strategy-chart-desktop-legend__text')
                .text(() => {
                    return this.$t('investmentStrategyChart.expectationAverage');
                })
                .attr('x', 30)
                .attr('y', 42)
                .attr('fill', '#50585B');

            this.legend.append('rect')
                .attr('class', 'investment-strategy-chart-desktop-legend__rect')
                .attr('width', 15)
                .attr('height', 15)
                .attr('x', 10)
                .attr('y', 50)
                .attr('fill', '#FF8C28');

            this.legend.append('text')
                .attr('class', 'investment-strategy-chart-desktop-legend__text')
                .text(() => {
                    return this.$t('investmentStrategyChart.expectationLow');
                })
                .attr('x', 30)
                .attr('y', 62)
                .attr('fill', '#50585B');
        },
        createSvg (chartRef) {
            if (this.chart) {
                this.chart.remove();
            };
            this.chart = d3.select(chartRef)
                .append('svg')
                .attr('width', this.chartWidth + this.margin.left + this.margin.right)
                .attr('height', this.chartHeight + this.margin.top + this.margin.bottom);

            // Create defs/filters
            const defs = this.chart.append('defs');
            const filter = defs
                .append('filter')
                .attr('id', 'boxshadow');
            filter.append('feGaussianBlur')
                .attr('in', 'SourceAlpha')
                .attr('stdDeviation', 2)
                .attr('result', 'blur');
            filter.append('feOffset')
                .attr('in', 'blur')
                .attr('dx', 2)
                .attr('dy', 4)
                .attr('result', 'offsetBlur');
            filter.append('feFlood')
                .attr('in', 'offsetBlur')
                .attr('flood-color', 'gray')
                .attr('flood-opacity', '0.25')
                .attr('result', 'offsetColor');
            filter.append('feComposite')
                .attr('in', 'offsetColor')
                .attr('in2', 'offsetBlur')
                .attr('operator', 'in')
                .attr('result', 'offsetBlur');
            const feMerge = filter.append('feMerge');
            feMerge.append('feMergeNode')
                .attr('in', 'offsetBlur');
            feMerge.append('feMergeNode')
                .attr('in', 'SourceGraphic');

            // Attach clip path for masking
            defs.append('clipPath')
                .attr('id', 'chartClipPath')
                .append('rect')
                .attr('height', this.chartHeight)
                .attr('width', this.chartWidth)
                .attr('transform', 'translate(' + this.margin.left + ', ' + this.margin.top + ')');

            // Draw Background
            this.chart.append('rect')
                .attr('class', 'background')
                .attr('height', this.chartHeight)
                .attr('width', this.chartWidth)
                .attr('fill', '#EAEAEA')
                .attr('transform', 'translate(' + this.margin.left + ', ' + this.margin.top + ')');
        },
        determineDimensions (chartRef) {
            const currentWidth = parseInt(d3.select(chartRef).style('width'), 10);
            this.chartWidth = currentWidth - this.margin.left - this.margin.right;

            this.chartHeight = (this.viewportNoMatch ? this.defaultChartHeightXs : this.defaultChartHeightLg) - this.margin.top - this.margin.bottom;
        },
        drawChart (forceRerender = false) {
            const chartRef = this.$refs.investmentStrategyChart;
            if (!chartRef || !this.investmentDiagramResponse) {
                return;
            }

            if (!this.chart || forceRerender) {
                this.determineDimensions(chartRef);
                this.createSvg(chartRef);
            }

            this.createScales();
            this.drawAxis();
            this.drawGridLines();
            this.drawInvestmentHorizonInfo();
            this.drawLegendForDesktop();
            this.drawLines(forceRerender);
            this.drawAreas();

            // Remove exising/add new mouse handlers
            this.chart.on('click', null);
            this.chart.on('click', this.onChartClick);

            // Activate last tick
            const lastTickX = this.chartData.length - 1;
            this.drawPointsAndLabels(lastTickX, this.defaultTransition);
        },
    },
    mounted () {
        this.updateData({ reset: true });
    },
};
</script>

<style scoped lang="scss">
  .investment-strategy-chart {
    margin-bottom: 20px;

    ::v-deep svg {
      cursor: pointer;
    }

    ::v-deep .grid-lines {
      .domain {
        display: none;
      }
      .tick {
        &:nth-child(2),
        &:last-child {
          display: none;
        }

        line {
          stroke-width: 3px;
          stroke: #ffffff;
        }
      }
    }

    ::v-deep .investment-strategy-chart__loader {
      pointer-events: none;
      opacity: 0;
      position: absolute;
      z-index: 1;
      background: #ffffff url('~@/assets/images/loader.gif') center center no-repeat;
      background-size: 60px 60px;
      height: 100%;
      width: 100%;
      transition: opacity 0.2s ease-in-out;
      transition-delay: 0.2s;

      &.investment-strategy-chart__loader--active {
          opacity: 0.45;
      }
    }

    ::v-deep .axis-x {
      @include font-migrosbank();
      font-size: 14px;
      line-height: 17px;

      .domain {
        display: none;
      }
    }

    ::v-deep .axis-y {
      @include font-migrosbank-bold();
      font-size: 14px;
      line-height: 17px;

      .domain {
        display: none;
      }

      @include media-breakpoint-down(xs) {
        display: none;
      }
    }

    ::v-deep .currency,
    ::v-deep .time-unit {
        @include font-migrosbank();
        font-size: 14px;
    }

    ::v-deep .area-low {
      fill: #FF8C28;
      opacity: 0.2;
    }

    ::v-deep .area-high {
      fill: #4F1F68;
      opacity: 0.2;
    }

    ::v-deep .label__text {
      @include font-migrosbank-bold();
      font-size: 14px;
      line-height: 16px;
    }
  }

  .investment-strategy-chart__wrapper {
    position: relative;
  }

  .investment-strategy-chart__infos {
    background: $mb-grey-12;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
    margin-bottom: 20px;
    padding: 15px 20px;
    font-size: 14px;
    line-height: 22px;
    @include font-migrosbank();
    color: $mb-grey5;

    @include media-breakpoint-down(xs) {
      font-size: 12px;
      line-height: 16px;
      padding: 10px;
    }
  }

  .investment-strategy-chart__infos--no-margin {
    margin-bottom: 0
  }

  .investment-strategy-chart__legend  {
    margin-top: 20px;
    font-size: 12px;
    @include  font-migrosbank-bold();
    color: $mb-grey5;

    @include media-breakpoint-down(xs) {
      margin-top: 10px;
      margin-bottom: 10px;
      background: $mb-grey-12;
      min-height: 35px;
    }

    @include media-breakpoint-up(lg) {
      display: none;
    }

    ul {
      margin-bottom: 0;
    }
  }

  .investment-strategy-chart__legend-item {
    display:  inline-block;

    @include media-breakpoint-down(xs) {
      font-size: 11px;
      width: 33.3%;
      line-height: 1.2;
    }

    & ~.investment-strategy-chart__legend-item {
      margin-left:  20px;

      @include media-breakpoint-down(xs) {
        margin-left: 0;
      }
    }
  }

  .investment-strategy-chart__legend-color {
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 8px;
    vertical-align: middle;

      @include media-breakpoint-down(xs) {
        min-height: 35px;
        margin-right: 0;
        width: 5px;
        margin-left: 0;
        vertical-align: middle;
      }
  }

  .investment-strategy-chart__legend-text {

    @include media-breakpoint-down(xs) {
      vertical-align: middle;
      display: inline-block;
      max-width: 30%;
      padding-left: 5px;
    }
  }

  .investment-strategy-chart__legend-color--high {
    background: $mb-violet;
  }
  .investment-strategy-chart__legend-color--average {
    background: $mb-pink;
  }
  .investment-strategy-chart__legend-color--low {
    background:  $mb-orange;
  }

  ::v-deep .investment-strategy-chart-desktop-legend__box {
    @include media-breakpoint-down(xs) {
      display: none;
    }
  }

  ::v-deep .investment-strategy-chart-desktop-legend__text {
    font-size: 12px;
    @include font-migrosbank();
    color: $mb-grey5;
  }

  ::v-deep .investment-horizon-info-group {
    @include media-breakpoint-up(lg) {
      display: none;
    }
  }

  ::v-deep .investment-horizon-info__text {
    @include font-migrosbank-bold();
    color: $mb-grey5;
    font-size: 10px;
  }

  ::v-deep .investment-horizon-info__amount {
    @include font-migrosbank();
    color: $mb-grey5;
    font-size: 14px;
  }
</style>
